import type { ComponentType } from 'react';
import {
  Workflow, Brain, FileStack, Network, ShieldCheck, BookOpenCheck,
  Search, ClipboardCheck, PenTool, Cog, Rocket, LifeBuoy,
} from 'lucide-react';

type Icon = ComponentType<{ className?: string }>;

export type ServiceItem = { icon: Icon; title: string; description: string };
export type ProcessStep = { step: number; title: string; description: string; icon: Icon };

export const coreServices: ServiceItem[] = [
  { icon: Workflow, title: 'Workflow Automation',
    description: 'Purpose-built automation that reduces repetitive work, approvals, and handoffs around the tools your team already uses.' },
  { icon: Brain, title: 'Private Document Intelligence',
    description: 'Secure AI that searches internal knowledge, policies, and records while keeping access, controls, and data handling shaped around your environment.' },
  { icon: Network, title: 'Systems Integration',
    description: 'APIs, connectors, and webhooks that link your business platforms, legacy systems, and custom applications into one coordinated workflow.' },
  { icon: FileStack, title: 'Records Modernization',
    description: 'Convert paper archives and fragmented records into organized, searchable information your team can access and manage.' },
  { icon: Search, title: 'Specialized AI Assistants',
    description: 'Focused AI assistants designed for the tasks, terminology, and workflows of your specific industry.' },
  { icon: BookOpenCheck, title: 'Handbooks & SOPs',
    description: "Auto-updating procedures and playbooks that capture your team's expertise and stay current as processes, systems, and requirements change." },
];

export const whyChoosePillars: ServiceItem[] = [
  { icon: ShieldCheck, title: 'Privacy-first Architecture',
    description: 'Solutions designed around security and ownership, so your data stays protected and under your control.' },
  { icon: Rocket, title: 'Scalable Infrastructure',
    description: "A foundation that grows with your organization and expands across departments and locations." },
  { icon: LifeBuoy, title: 'Long-term Support',
    description: 'We partner with you beyond implementation to make sure solutions keep delivering real value.' },
  { icon: Network, title: 'Flexible, Secure Deployment',
    description: 'We work in the environment that best fits your organization and compliance needs, private cloud or on-prem.' },
];

export const successProcess: ProcessStep[] = [
  { step: 1, title: 'Discovery', icon: Search,
    description: 'We sit down with your team to understand how information and processes actually move day-to-day, where the friction is, and what already works.' },
  { step: 2, title: 'Assessment', icon: ClipboardCheck,
    description: 'We map your current tools, systems, and workflows to find where automation saves the most time and risk, without adding or taking anything out.' },
  { step: 3, title: 'Tailored Design', icon: PenTool,
    description: 'We design automation that works alongside the systems you already have. No costly overhaul or months of development required.' },
  { step: 4, title: 'Solution Engineering', icon: Cog,
    description: "We configure our tools within your environment so the right data is collected and organized, with little to no training needed for your staff." },
  { step: 5, title: 'Private Deployment', icon: Rocket,
    description: 'We roll out the solution in phases, fitting into how your team already operates, without disrupting daily operations.' },
  { step: 6, title: 'Continued Support', icon: LifeBuoy,
    description: 'We stay engaged as your ongoing partner: refining, supporting, and expanding the solution as your needs change.' },
];

